import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Btn } from '../../components/Btn';
import { Card } from '../../components/Card';
import { ProductSelect } from '../../components/ProductSelect';
import { Select } from '../../components/Select';
import {
  listBranches,
  listProducts,
  listStockByBranch,
} from '../../data/fetchers';
import { fmtStockBreakdown } from '../../lib/format';
import { visibleBranchIds } from '../../lib/permissions';
import { toBaseQty } from '../../lib/stockUnits';
import { createTransfer } from '../../lib/transfers';
import { useSession } from '../../stores/session';
import { StockHeader } from './StockHeader';

interface Line {
  productId: string;
  unit: string;
  qty: number;
  searchQuery?: string;
}

export function TransferCreateView() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: branches = [] } = useQuery({
    queryKey: ['branches'],
    queryFn: listBranches,
  });
  const { data: products = [] } = useQuery({
    queryKey: ['products'],
    queryFn: listProducts,
  });
  const user = useSession((s) => s.user);
  // Source must be a branch the user controls (can't drain stock from foreign branches).
  const allowedFrom = user
    ? visibleBranchIds(
        user,
        branches.map((b) => b.id),
      )
    : [];
  const fromBranches = branches.filter((b) => allowedFrom.includes(b.id));

  const [fromBranchId, setFrom] = useState('');
  const [toBranchId, setTo] = useState('');
  const [lines, setLines] = useState<Line[]>([
    { productId: '', unit: '', qty: 1, searchQuery: '' },
  ]);
  const [error, setError] = useState('');

  // Scanner & Barcode states
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const [barcodeQuery, setBarcodeQuery] = useState('');
  const [scanFeedback, setScanFeedback] = useState('');

  const playBeep = () => {
    try {
      const AudioCtx =
        window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(1000, ctx.currentTime);
      gain.gain.setValueAtTime(0.1, ctx.currentTime);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.08);
    } catch (e) {
      console.warn('Audio beep failed', e);
    }
  };

  const handleBarcodeSearch = (code: string) => {
    let foundProduct = null;
    let foundUnit = '';
    for (const p of products) {
      for (const u of p.units) {
        if (u.barcode === code.trim()) {
          foundProduct = p;
          foundUnit = u.unit;
          break;
        }
      }
      if (foundProduct) break;
    }

    if (foundProduct) {
      playBeep();
      // Find if there is an empty line to populate, otherwise append a new line
      const emptyIdx = lines.findIndex((l) => !l.productId);
      if (emptyIdx !== -1) {
        setLine(emptyIdx, {
          productId: foundProduct.id,
          unit: foundUnit,
          qty: 1,
        });
      } else {
        setLines((ls) => [
          ...ls,
          { productId: foundProduct!.id, unit: foundUnit, qty: 1 },
        ]);
      }
      setScanFeedback(`✓ เพิ่มสินค้า: ${foundProduct.name} (${foundUnit})`);
      setBarcodeQuery('');
      setTimeout(() => setScanFeedback(''), 5000);
      return true;
    } else {
      setScanFeedback(`❌ ไม่พบบาร์โค้ดรหัส: "${code}"`);
      setTimeout(() => setScanFeedback(''), 5000);
      return false;
    }
  };

  useEffect(() => {
    if (!isScanning) return;

    let stream: MediaStream | null = null;
    let scanInterval: any = null;

    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: 'environment' } })
      .then((s) => {
        stream = s;
        if (videoRef.current) {
          videoRef.current.srcObject = s;
          videoRef.current
            .play()
            .catch((e) => console.warn('Video play error', e));
        }

        if ('BarcodeDetector' in window) {
          const detector = new (window as any).BarcodeDetector({
            formats: ['ean_13', 'ean_8', 'qr_code', 'code_128'],
          });

          scanInterval = setInterval(async () => {
            if (
              videoRef.current &&
              videoRef.current.readyState === videoRef.current.HAVE_ENOUGH_DATA
            ) {
              try {
                const codes = await detector.detect(videoRef.current);
                if (codes.length > 0) {
                  const raw = codes[0].rawValue;
                  handleBarcodeSearch(raw);
                  setIsScanning(false);
                }
              } catch (e) {
                console.error('Barcode detection error', e);
              }
            }
          }, 400);
        }
      })
      .catch((err) => {
        console.error('Camera access error', err);
        setCameraError(
          'ไม่สามารถเข้าถึงกล้องถ่ายรูปได้ กรุณาตรวจสอบสิทธิ์การเข้าใช้งานกล้องในเบราว์เซอร์ของคุณ',
        );
      });

    return () => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
      if (scanInterval) {
        clearInterval(scanInterval);
      }
    };
  }, [isScanning]);

  const { data: sourceStock = [] } = useQuery({
    queryKey: ['stock', fromBranchId],
    queryFn: () => listStockByBranch({ branchId: fromBranchId || null }),
    enabled: !!fromBranchId,
  });
  const onHandOf = (productId: string) =>
    sourceStock.find((s) => s.productId === productId)?.onHand ?? 0;
  const productOf = (id: string) => products.find((p) => p.id === id) ?? null;
  const shortOf = (b: string) => branches.find((x) => x.id === b)?.short ?? b;

  const mutation = useMutation({
    mutationKey: ['transfer-create'],
    meta: { errorRetry: true },
    mutationFn: createTransfer,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['stock'] });
      void qc.invalidateQueries({ queryKey: ['transfers'] });
      void qc.invalidateQueries({ queryKey: ['movements'] });
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
      navigate('/transfers');
    },
  });

  const setLine = (i: number, patch: Partial<Line>) =>
    setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  // Selecting a product resets the line's unit to that product's first unit and syncs search query.
  const setLineProduct = (i: number, id: string) => {
    const p = productOf(id);
    setLine(i, {
      productId: id,
      unit: p?.units[0]?.unit ?? '',
      searchQuery: p ? p.name : '',
    });
  };
  const addLine = () =>
    setLines((ls) => [...ls, { productId: '', unit: '', qty: 1 }]);
  const removeLine = (i: number) =>
    setLines((ls) => ls.filter((_, j) => j !== i));

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setError('');
    const valid = lines.filter((l) => l.productId && l.unit && l.qty > 0);
    if (
      !fromBranchId ||
      !toBranchId ||
      fromBranchId === toBranchId ||
      valid.length === 0
    )
      return;
    if (!allowedFrom.includes(fromBranchId)) return; // defense: source must be in scope
    const baseOf = (l: Line) => {
      const p = productOf(l.productId);
      return p ? toBaseQty(p, l.unit, l.qty) : 0;
    };
    const over = valid.find((l) => baseOf(l) > onHandOf(l.productId));
    if (over) {
      const p = productOf(over.productId);
      setError(
        `โอน "${p?.name ?? over.productId}" เกินคงเหลือต้นทาง (${onHandOf(over.productId)})`,
      );
      return;
    }
    mutation.mutate({
      requestId: crypto.randomUUID(),
      fromBranchId,
      toBranchId,
      lines: valid.map((l) => {
        const product = productOf(l.productId);
        const unit = product?.units.find(
          (candidate) => candidate.unit === l.unit,
        );
        return {
          productId: l.productId,
          unitId: unit?.id ?? `${l.productId}:base`,
          quantity: baseOf(l),
        };
      }),
    });
  };

  return (
    <div className="bo-fade w-full">
      <StockHeader
        title="สร้างใบโอน"
        subtitle="โอนสินค้าจากสาขาต้นทางไปปลายทาง"
        status={{
          label: `${lines.filter((line) => line.productId).length} รายการสินค้า`,
          tone: lines.some((line) => line.productId) ? 'ok' : 'neutral',
        }}
      />
      <form onSubmit={onSubmit} className="w-full flex flex-col gap-6">
        {/* Branch Info Card */}
        <Card title="ข้อมูลสาขาที่โอน">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="flex flex-col gap-1.5">
              <span className="text-sm-label font-semibold text-ink-2">
                จากสาขาต้นทาง <span className="text-danger">*</span>
              </span>
              <select
                aria-label="จากสาขา"
                value={fromBranchId}
                onChange={(e) => setFrom(e.target.value)}
                className="h-10 px-3 rounded-btn border border-line bg-surface text-body focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
              >
                <option value="">— เลือกสาขาต้นทาง —</option>
                {fromBranches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.short}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex flex-col gap-1.5">
              <span className="text-sm-label font-semibold text-ink-2">
                ไปยังสาขาปลายทาง <span className="text-danger">*</span>
              </span>
              <select
                aria-label="ไปสาขา"
                value={toBranchId}
                onChange={(e) => setTo(e.target.value)}
                className="h-10 px-3 rounded-btn border border-line bg-surface text-body focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
              >
                <option value="">— เลือกสาขาปลายทาง —</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.short}
                  </option>
                ))}
              </select>
            </label>

            {fromBranchId && toBranchId && fromBranchId === toBranchId && (
              <div className="md:col-span-2 text-xs text-danger font-semibold bg-danger/5 px-3 py-2.5 rounded-btn border border-danger/10 leading-relaxed">
                ⚠️ สาขาต้นทางและปลายทางต้องไม่เป็นสาขาเดียวกัน
              </div>
            )}
          </div>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
          {/* Left Column: Item list table */}
          <div className="lg:col-span-8 flex flex-col gap-4">
            {/* Barcode scan section */}
            <Card title="สแกนบาร์โค้ดสินค้า">
              <div className="flex flex-col gap-1.5">
                <span className="text-sm-label font-semibold text-ink-2">
                  สแกนหรือกรอกบาร์โค้ดเพื่อเพิ่มรายการ
                </span>
                <div className="grid grid-cols-2 gap-2 sm:flex">
                  <div className="relative col-span-2 sm:flex-1">
                    <input
                      type="text"
                      placeholder="กรอกหรือยิงรหัสบาร์โค้ดเพื่อเพิ่มเข้าใบโอน..."
                      value={barcodeQuery}
                      onChange={(e) => setBarcodeQuery(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          if (barcodeQuery) handleBarcodeSearch(barcodeQuery);
                        }
                      }}
                      className="h-10 w-full pl-3 pr-10 rounded-btn border border-line bg-surface text-body focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all num"
                    />
                    {barcodeQuery && (
                      <button
                        type="button"
                        onClick={() => setBarcodeQuery('')}
                        className="absolute right-3 top-2.5 text-ink-3 hover:text-ink"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                  <Btn
                    type="button"
                    kind="default"
                    onClick={() => handleBarcodeSearch(barcodeQuery)}
                    disabled={!barcodeQuery}
                  >
                    ค้นหา
                  </Btn>
                  <Btn
                    type="button"
                    kind="primary"
                    className="bg-accent hover:bg-accent-dark text-white shadow-sm"
                    onClick={() => {
                      setIsScanning(true);
                      setCameraError('');
                    }}
                    icon={
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M12 4v1m6 11h2m-6 0h-2v4m0-16v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z"
                        />
                      </svg>
                    }
                  >
                    สแกนกล้อง
                  </Btn>
                </div>
                {scanFeedback && (
                  <div
                    className={`text-xs font-semibold px-3 py-2 rounded-btn mt-1 transition-all ${
                      scanFeedback.startsWith('✓')
                        ? 'bg-ok-light text-ok border border-ok/10'
                        : 'bg-danger-light text-danger border border-danger/10'
                    }`}
                  >
                    {scanFeedback}
                  </div>
                )}
              </div>
            </Card>

            <Card title="รายการสินค้าที่จะโอน">
              <div className="overflow-visible">
                <table className="w-full border-collapse">
                  <thead className="hidden md:table-header-group">
                    <tr className="border-b border-line text-sm-label text-ink-3 uppercase font-bold">
                      <th className="pb-3 text-left pl-1">สินค้า</th>
                      <th className="pb-3 text-left w-24">จำนวน</th>
                      <th className="pb-3 text-left w-28">หน่วย</th>
                      <th className="pb-3 text-left w-48">คงเหลือต้นทาง</th>
                      <th className="pb-3 text-right pr-1 w-12"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-line-2">
                    {lines.map((l, i) => {
                      const p = productOf(l.productId);
                      const onHand = onHandOf(l.productId);
                      const baseQty = p ? toBaseQty(p, l.unit, l.qty) : 0;
                      const isOver =
                        fromBranchId && l.productId && baseQty > onHand;

                      return (
                        <tr
                          key={i}
                          className="grid grid-cols-12 md:table-row py-4 md:py-0 border-b border-line-2 md:border-none gap-x-2 gap-y-3 md:gap-0"
                        >
                          <td className="col-span-12 md:table-cell py-1.5 md:py-3 pr-0 md:pr-2 pl-1">
                            <span className="md:hidden block text-xs font-bold text-ink-3 mb-1">
                              สินค้า
                            </span>
                            <ProductSelect
                              products={products}
                              value={l.productId}
                              onChange={(id) => setLineProduct(i, id)}
                            />
                          </td>

                          <td className="col-span-3 md:table-cell py-1.5 md:py-3 pr-0 md:pr-2">
                            <span className="md:hidden block text-xs font-bold text-ink-3 mb-1">
                              จำนวน
                            </span>
                            <input
                              aria-label={`จำนวนแถว ${i + 1}`}
                              type="number"
                              min={1}
                              value={l.qty}
                              onChange={(e) =>
                                setLine(i, { qty: Number(e.target.value) })
                              }
                              className="h-10 w-full px-3 rounded-btn border border-line bg-surface text-body num focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                              placeholder="จำนวน"
                            />
                          </td>

                          <td className="col-span-3 md:table-cell py-1.5 md:py-3 pr-0 md:pr-2">
                            <span className="md:hidden block text-xs font-bold text-ink-3 mb-1">
                              หน่วย
                            </span>
                            <select
                              aria-label={`หน่วยแถว ${i + 1}`}
                              value={l.unit}
                              onChange={(e) =>
                                setLine(i, { unit: e.target.value })
                              }
                              disabled={!l.productId}
                              className="h-10 w-full px-3 rounded-btn border border-line bg-surface text-body focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all disabled:bg-bg-warm disabled:text-ink-3"
                            >
                              {(p?.units ?? []).map((u) => (
                                <option key={u.unit} value={u.unit}>
                                  {u.unit}
                                </option>
                              ))}
                            </select>
                          </td>

                          <td className="col-span-5 md:table-cell py-1.5 md:py-3 pr-0 md:pr-2">
                            <span className="md:hidden block text-xs font-bold text-ink-3 mb-1">
                              คงเหลือต้นทาง
                            </span>
                            <div className="text-body flex flex-col justify-center min-h-[40px]">
                              {fromBranchId && l.productId && p ? (
                                isOver ? (
                                  <span className="text-danger font-semibold text-xs leading-tight">
                                    คงเหลือ: {fmtStockBreakdown(onHand, p)}
                                    <span className="block text-sm-label text-danger/80">
                                      สินค้าไม่พอโอน
                                    </span>
                                  </span>
                                ) : (
                                  <span className="text-ink-2 text-xs">
                                    คงเหลือ:{' '}
                                    <span className="num font-semibold text-ink">
                                      {fmtStockBreakdown(onHand, p)}
                                    </span>
                                  </span>
                                )
                              ) : (
                                <span className="text-ink-4 text-xs">—</span>
                              )}
                            </div>
                          </td>

                          <td className="col-span-1 md:table-cell py-1.5 md:py-3 text-right pr-1 flex items-end justify-center pb-0.5">
                            <div className="flex justify-end pb-0.5">
                              <button
                                type="button"
                                onClick={() => removeLine(i)}
                                disabled={lines.length === 1}
                                className="h-10 w-10 inline-flex items-center justify-center rounded-btn text-ink-3 hover:text-danger hover:bg-danger/10 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-ink-3 transition-colors"
                                title="ลบรายการ"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="18"
                                  height="18"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                  />
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 pt-4 border-t border-line-2 flex justify-start">
                <Btn kind="default" size="sm" type="button" onClick={addLine}>
                  + เพิ่มรายการสินค้า
                </Btn>
              </div>
            </Card>
          </div>

          {/* Right Column: Branch Selection & Summary */}
          <div className="lg:col-span-4 flex flex-col gap-6">
            {/* Transfer Summary Card */}
            <Card title="สรุปการส่งโอน">
              <div className="flex flex-col gap-4">
                <div className="flex justify-between items-center text-body">
                  <span className="text-ink-3">จากต้นทาง:</span>
                  <span className="font-semibold text-ink">
                    {fromBranchId ? shortOf(fromBranchId) : '—'}
                  </span>
                </div>

                <div className="flex justify-between items-center text-body">
                  <span className="text-ink-3">ไปปลายทาง:</span>
                  <span className="font-semibold text-ink">
                    {toBranchId ? shortOf(toBranchId) : '—'}
                  </span>
                </div>

                <div className="flex justify-between items-center text-body">
                  <span className="text-ink-3">รายการสินค้า:</span>
                  <span className="font-semibold text-ink">
                    {lines.filter((l) => l.productId).length} รายการ
                  </span>
                </div>

                {error && (
                  <div className="text-xs text-danger font-semibold bg-danger/5 px-3 py-2.5 rounded-btn border border-danger/10 leading-relaxed">
                    {error}
                  </div>
                )}

                <div className="border-t border-line-2 my-1"></div>

                <div className="flex gap-3">
                  <Btn
                    kind="default"
                    type="button"
                    onClick={() => navigate('/transfers')}
                    className="flex-1"
                  >
                    ยกเลิก
                  </Btn>
                  <Btn
                    kind="primary"
                    type="submit"
                    disabled={
                      !fromBranchId ||
                      !toBranchId ||
                      fromBranchId === toBranchId ||
                      lines.filter((l) => l.productId).length === 0 ||
                      mutation.isPending
                    }
                    className="flex-1"
                  >
                    {mutation.isPending ? 'กำลังส่ง...' : 'สร้างและส่ง'}
                  </Btn>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </form>

      {/* Camera scan fullscreen interface */}
      {isScanning && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="transfer-scanner-title"
          aria-describedby="transfer-scanner-description"
          className="fixed inset-0 z-50 isolate overflow-y-auto bg-neutral-950"
        >
          <style
            dangerouslySetInnerHTML={{
              __html: `
            @keyframes scanLaser {
              0% { top: 0%; }
              50% { top: 100%; }
              100% { top: 0%; }
            }
          `,
            }}
          />

          {/* Scanning Guide Overlay */}
          <div className="relative z-10 mx-auto flex min-h-dvh w-full max-w-lg flex-col gap-4 px-4 pt-[max(1rem,env(safe-area-inset-top))] pb-[max(1rem,env(safe-area-inset-bottom))] text-center sm:px-6">
            <div className="flex w-full shrink-0 items-center justify-between gap-3 rounded-xl bg-neutral-900 p-3">
              <h2
                id="transfer-scanner-title"
                className="text-h3 font-extrabold text-white"
              >
                สแกนบาร์โค้ดสินค้า
              </h2>
              <button
                type="button"
                aria-label="ปิดหน้าต่างสแกนบาร์โค้ด"
                onClick={() => setIsScanning(false)}
                className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white text-neutral-950 text-xl transition-colors hover:bg-neutral-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white"
              >
                ✕
              </button>
            </div>

            {/* Scanning Target Box */}
            <div className="flex min-h-56 flex-1 items-center justify-center py-4">
              <div className="relative h-48 w-48 overflow-hidden rounded-xl border-2 border-white sm:h-64 sm:w-64">
                {/* Laser Line */}
                <div className="absolute left-0 right-0 h-0.5 bg-danger shadow-[0_0_8px_rgba(200,54,42,0.8)] animate-[scanLaser_2s_infinite]"></div>
              </div>
            </div>
            <div
              id="transfer-scanner-description"
              className="rounded-xl bg-neutral-900 px-4 py-3 text-sm font-semibold leading-relaxed text-white"
            >
              วางบาร์โค้ดให้อยู่ในกรอบสี่เหลี่ยมเพื่อสแกนอัตโนมัติ
            </div>
            {/* Simulated Scanner Fallback */}
            {!('BarcodeDetector' in window) && !cameraError && (
              <div className="flex shrink-0 flex-col gap-3 rounded-xl border border-neutral-500 bg-neutral-900 p-4 text-left text-sm text-white">
                <div className="flex gap-2 items-start text-amber-300 font-bold">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="2.5"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                  <span>เบราว์เซอร์นี้สแกนบาร์โค้ดด้วยกล้องไม่ได้</span>
                </div>
                <span className="text-neutral-100">
                  กรุณาพิมพ์เลขบาร์โค้ด หรือใช้เครื่องยิงบาร์โค้ดแทน
                </span>
                {import.meta.env.DEV && (
                  <Select
                    aria-label="จำลองการสแกนสินค้า"
                    className="!border-neutral-400 !bg-neutral-800 !text-white [color-scheme:dark]"
                    wrapperClassName="text-white"
                    onChange={(e) => {
                      if (e.target.value) {
                        handleBarcodeSearch(e.target.value);
                        setIsScanning(false);
                      }
                    }}
                  >
                    <option value="" className="bg-ink">
                      --- เลือกสินค้าเพื่อจำลองการสแกน ---
                    </option>
                    {products.map((p) =>
                      p.units.map((u) => (
                        <option
                          key={u.barcode}
                          value={u.barcode}
                          className="bg-ink"
                        >
                          {p.name} ({u.unit}) - {u.barcode}
                        </option>
                      )),
                    )}
                  </Select>
                )}
              </div>
            )}
          </div>

          {/* Camera Video Feed */}
          <video
            ref={videoRef}
            className="absolute inset-0 z-0 w-full h-full object-cover brightness-[.65]"
            playsInline
            muted
          />

          {cameraError && (
            <div className="absolute inset-0 bg-black/90 flex flex-col items-center justify-center p-6 text-center z-30">
              <span className="text-danger text-lg font-bold mb-2">
                เกิดข้อผิดพลาดในการโหลดกล้อง
              </span>
              <span className="text-white text-sm mb-6 max-w-sm">
                {cameraError}
              </span>
              <Btn kind="default" onClick={() => setIsScanning(false)}>
                ปิดหน้าต่างสแกน
              </Btn>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
